import { useEffect, useRef, useState } from 'react'

export interface Microphone {
  deviceId: string
  label: string
}

export const useMicrophones = (startMuted: boolean) => {
  const [, setMicrophones] = useState<Microphone[]>([])
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [isMuted, setIsMuted] = useState<boolean>(startMuted)
  const [isRecording, setIsRecording] = useState(false)
  const [isRecordingPaused, setIsRecordingPaused] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [selectedMicrophone, setSelectedMicrophone] =
    useState<Microphone | null>(null)
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<
    boolean | null
  >(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)

  const getMicrophones = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId}`,
        }))

      setMicrophones(audioInputs)
      return audioInputs
    } catch (error) {
      console.error('Error fetching microphones:', error)
    }
  }

  const connectMicrophone = async (force = false) => {
    try {
      // Check microphone permission before connecting
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })

        if (permissionStatus.state === 'denied') {
          setHasMicrophonePermission(false)
          console.error('Microphone permission denied')
          return null
        }

        if (permissionStatus.state === 'granted') {
          setHasMicrophonePermission(true)
        }
        // If state is 'prompt', permission will be requested when getUserMedia is called
      } catch (permErr) {
        console.error('Error checking microphone permission:', permErr)
        // Continue anyway - fallback for browsers that don't support permissions API
      }

      const devices = await getMicrophones()

      if (stream && !force) {
        stream.getTracks().forEach(track => track.stop())
      }

      const defaultMicrophone =
        devices?.find(d => d.deviceId === 'default') || devices?.[0]

      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      })

      newStream.getAudioTracks().forEach(track => {
        track.enabled = !isMuted
      })

      setSelectedMicrophone(defaultMicrophone || null)
      setStream(newStream)
      setHasMicrophonePermission(true)

      return newStream
    } catch (error) {
      console.error('Error accessing microphone:', error)

      // Check if the error is due to permission denial
      if (error instanceof Error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          setHasMicrophonePermission(false)
        }
      }

      return null
    }
  }

  const muteMicrophone = (mute: boolean) => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !mute
      })
      setIsMuted(mute)
    }
  }

  const handleConnection = () => {
    connectMicrophone().catch(error => {
      console.error('Error connecting to microphone:', error)

      // Check if the error is due to permission denial
      if (error instanceof Error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          setHasMicrophonePermission(false)
        }
      }
    })
  }

  const pickMimeType = () => {
    const candidates = [
      'audio/mp4', // Safari (AAC)
      'audio/webm;codecs=opus', // Chrome / Edge
      'audio/ogg;codecs=opus', // Firefox
    ]
    return candidates.find(t => MediaRecorder.isTypeSupported(t)) || ''
  }

  const startRecording = async () => {
    try {
      const microphoneStream = await connectMicrophone(true)

      await new Promise(r => setTimeout(r, 250))

      // use the stream returned from connectMicrophone
      if (!microphoneStream) {
        throw new Error('No microphone stream available for recording')
      }

      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(
        microphoneStream,
        mimeType ? { mimeType } : undefined,
      )

      const chunks: Blob[] = []

      recorder.addEventListener('dataavailable', e => {
        if (e.data.size > 0) chunks.push(e.data)
      })

      recorder.addEventListener('stop', () => {
        // check: ensure we have data before creating blob
        if (chunks.length === 0) {
          console.error(
            'No audio recording data available - chunks array is empty',
          )
          setAudioBlob(new Blob([], { type: mimeType || 'audio/webm' }))
          return
        }

        const blob = new Blob(chunks, { type: mimeType || chunks[0]?.type })

        // check: ensure blob has content
        if (blob.size === 0) {
          console.error('Audio recording blob is empty (0 bytes)')
          setAudioBlob(blob)
          return
        }

        console.log(`Audio recording completed: ${blob.size} bytes`)
        setAudioBlob(blob)
        chunks.length = 0
        microphoneStream.getTracks().forEach(t => t.stop())
      })

      // recording with 1 sec timeslice to ensure data is available
      recorder.start(1000)
      mediaRecorderRef.current = recorder
      setIsRecording(true)
    } catch (err) {
      console.error('Recording failed:', err)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      // check if MediaRecorder is in a state that allows requestData()
      if (
        mediaRecorderRef.current.state === 'recording' ||
        mediaRecorderRef.current.state === 'paused'
      ) {
        // request any remaining data before stopping
        mediaRecorderRef.current.requestData()
      }

      // small delay to ensure final part is processed, Zhenya issue
      setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== 'inactive'
        ) {
          mediaRecorderRef.current.stop()
        }
        setIsRecording(false)
        setIsRecordingPaused(false)
      }, 100)
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
    }
  }

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause()
      setIsRecordingPaused(true)
    }
  }

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume()
      setIsRecordingPaused(false)
    }
  }

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', handleConnection)

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleConnection,
      )
    }
  }, [selectedMicrophone])

  useEffect(
    () => () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
    },
    [stream],
  )

  return {
    selectedMicrophone,
    isMuted,
    stream,
    isRecording,
    isRecordingPaused,
    audioBlob,
    hasMicrophonePermission,
    connectMicrophone,
    muteMicrophone,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
  }
}

export interface UseMicrophonesReturnType {
  selectedMicrophone: Microphone | null
  isMuted: boolean
  stream: MediaStream | null
  isRecording: boolean
  isRecordingPaused: boolean
  audioBlob: Blob | null
  hasMicrophonePermission: boolean | null
  connectMicrophone: () => Promise<MediaStream | null>
  muteMicrophone: (mute: boolean) => void
  startRecording: () => Promise<void>
  stopRecording: () => void
  pauseRecording: () => void
  resumeRecording: () => void
}
